import { AppointmentServiceModel } from '@models/appointment-service.model';
import { InvoiceAddress } from '@models/invoice-address.model';
import { AdyenPaymentDetails } from '@models/adyen.model';
import { SpentAdyenSession } from '@models/booking-outcome.model';
import { FreeAppointmentDao } from '@models/availability.model';
import { CaptchaDbModel } from '@models/captcha.model';
import { CouponDbModel } from '@models/coupon-db.model';
import { MeetingTypesModel } from '@models/meeting-types.model';
import { PayPalOrderDetailModel } from '@models/partner-setting.model';
import { StoreDbModel } from '@models/store-db.model';
import { UserInfo } from '@models/user.model';
import { WorkerDbModel } from '@models/worker-db.model';

export class SelectedBookingDataModel {
  selectedMeetingTypes: MeetingTypesModel[] = [];
  selectedServices: AppointmentServiceModel[] = [];
  currentSelectedAppointmentState = new CurrentSelectedAppointmentState();
  cartItems: AppointmentCartItem[] = [];
  selectedUser: UserInfo;
  selectedCustomFieldValues: {
    custom_field_id: number,
    type: string,
    value?: any, file_ids?: any,
    supported_appointment_service_ids?: number[],
    supported_meeting_type_ids?: number[]
  }[] = [];
  totalBookingAmount: number;
  subTotal: number;
  totalDiscount: number;
  paymentMethodData = new PaymentMethodData();
  selectedCoupon: CouponDbModel;
  captcha: CaptchaDbModel;
  timezone: string | null = null;
}

export class CurrentSelectedAppointmentState {
  selectedStoreLeadGeneratorPostcode: string;
  // True only when the value above is the postcode the customer entered in the store search
  // (canonical `postcode_details.postcode`); false when it is a store's own `zip` written by a
  // booking question. The personal form and the postcode-filtered requests read the value only
  // when this flag is true.
  storeLeadGeneratorPostcodeEnteredByCustomer = false;
  // The normalised store-search postcode the personal form last wrote into `userInfo.zip`; null
  // when the zip was never filled from it. Lets a later change replace only what the widget filled.
  personalFormZipFilledFromPostcode: string | null = null;
  selectedMeetingTypeId: number;
  selectedStoreId: number;
  selectedStore: StoreDbModel;
  selectedStoreUuids: string[] = [];
  selectedServiceIds: number[] = [];
  selectedServices: AppointmentServiceModel[] = [];
  selectedWorkerId: number;
  selectedWorker: WorkerDbModel;
  selectedDate: Date;
  storeAndWorkerPreSelectedThroughWorkerSelectField = false
  // Which worker-select QUESTION named the advisor above. Scopes the advisor-driven option
  // restriction to that question's own child, so a card-select elsewhere in a partner's flow can
  // never be filtered by an advisor it has nothing to do with.
  workerSelectQuestionId: number;
  storePreSelectedThroughStoreSelectField = false;
  workers_view: 'card' | 'dropdown';
  storeDefaultWorkerIds = Array<number>();
}

export class AppointmentCartItem {
  cartItemId: string;
  storeId: number;
  store: StoreDbModel;
  date: Date;
  startTime: string;
  endTime: string;
  shortStart: string;
  startHour: number;
  workerId: number;
  worker: WorkerDbModel;
  workerUuids: string[];
  combiServiceWorkersMapping: Record<number, number>;
  serviceId: number;
  service: AppointmentServiceModel;
  selectedMeetingTypeId: number = null;
  isAppointmentReserved = false;
  appointmentDaoObject: FreeAppointmentDao;
}

export class PaymentMethodData {
  paymentType: string;
  stripePaymentMethodId: string;
  paypalOrderDetail: PayPalOrderDetailModel;
  // external_invoice only: the address the invoice goes to, and whether the form is complete
  invoiceAddress: InvoiceAddress | null = null;
  invoiceAddressValid = true;
  /** Opaque Adyen handles; the backend reads the real outcome from Adyen. */
  adyenPaymentDetails: AdyenPaymentDetails;
  /**
   * Every Adyen session a refused or unconfirmed booking has spent in this checkout, oldest first.
   * Kept here rather than on the summary so a customer who chose another time and failed again
   * still sees the first reference too.
   */
  spentAdyenSessions: SpentAdyenSession[] = [];
}
